import { addCalendarLabel } from 'owa-locstrings/lib/strings/addcalendarlabel.locstring.json';
import loc from 'owa-localize';
import * as React from 'react';
import { ControlIcons } from 'owa-control-icons';
import { Icon } from '@fluentui/react/lib/Icon';
import { LazyPublicFolderPicker } from 'owa-publicfolder-picker';
import { observer } from 'mobx-react';
import { DefaultButton } from '@fluentui/react/lib/Button';
import { getFlexContainerStyle } from './getFlexContainerStyle';
import { getPrimaryAndConnectedAccountsEmailAddresses } from 'owa-session-store';
import CalendarAccountGroup from './CalendarAccountGroup';
import { initializeCalendarPickerStore } from '../mutators/initializeStoreMutator';
import type CalendarPickerScenarioId from '../store/schema/CalendarPickerScenarioId';
import { getOWAAccountProvider, getOWAConnectedAccounts } from 'owa-accounts-store';

const styles = require('./CalendarPicker.scss');

/**
 * Calendar picker properties
 * itemClicked: click handler for calendar nodes
 */
export interface CalendarPickerProps {
    scenarioId: CalendarPickerScenarioId;
    className: string;
    showCalendarPicker: boolean;
    onClickAddCalendar?: () => void;
    onClickAccountSettings?: () => void;
    onClickAddTeamsCalendar?: () => void;
}

/**
 * The state for the calendar picker
 * isCreatingCalendarGroup: whether new calendar group is being created
 */
export interface CalendarPickerGroupState {
    isCreatingCalendarGroup: boolean;
}

@observer
export default class CalendarPicker extends React.Component<CalendarPickerProps, {}> {
    constructor(props: CalendarPickerProps) {
        super(props);

        initializeCalendarPickerStore(props.scenarioId);
    }

    render() {
        const { showCalendarPicker, onClickAddCalendar, onClickAddTeamsCalendar } = this.props;
        const flexContainerStyle = getFlexContainerStyle();

        const accountsSmtpAddresses = getPrimaryAndConnectedAccountsEmailAddresses();
        const connectedAccounts = getOWAConnectedAccounts();

        /*
         * Check to ensure connected accounts in the owa-accounts-store were properly loaded into owa-session-store.
         * If they were not, we should add the user identity to the calendar picker so the user knows they are in an error
         * state and is given the option to reconnect their account.
         */

        connectedAccounts.forEach(connectedAccount => {
            const userIdentity = connectedAccount.userIdentity;
            if (!accountsSmtpAddresses.includes(userIdentity)) {
                accountsSmtpAddresses.push(userIdentity);
            }
        });

        const hasMultipleAccounts = accountsSmtpAddresses.length > 1;

        return (
            <div className={styles.calendarPickersAndAddCalendar}>
                <div className={styles.spacedPickerButton}>
                    <DefaultButton
                        className={styles.calendarListDefaultButton}
                        onClick={onClickAddCalendar}
                        title={loc(addCalendarLabel)}
                        styles={flexContainerStyle}>
                        <Icon className={styles.listButtonIcon} iconName={ControlIcons.AddEvent} />
                        <div className={styles.listItemLabel}>{loc(addCalendarLabel)}</div>
                    </DefaultButton>
                </div>
                {showCalendarPicker && (
                    <>
                        <div className={styles.calendarPickers}>
                            {accountsSmtpAddresses.map(userIdentity => (
                                <CalendarAccountGroup
                                    key={userIdentity}
                                    scenarioId={this.props.scenarioId}
                                    userIdentity={userIdentity}
                                    accountProvider={getOWAAccountProvider(userIdentity)}
                                    hasMultipleAccounts={hasMultipleAccounts}
                                    className={this.props.className}
                                    onClickAccountSettings={this.props.onClickAccountSettings}
                                    onClickAddTeamsCalendar={onClickAddTeamsCalendar}
                                />
                            ))}
                        </div>
                        <LazyPublicFolderPicker />
                    </>
                )}
            </div>
        );
    }
}
